import React, { useMemo } from 'react';

import { useFormikContext } from 'formik';

import useCopy from 'lib/hooks/useCopy';
import { formatPhoneNumber } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Input from 'components/shared/Input/Input';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import ImageUploader from 'components/shared/ImageUploader/ImageUploader';
import SectionList from 'components/practice/UserPreferences/components/SectionList';
import PracticeNameInput from 'components/shared/PracticeNameInput/PracticeNameInput';

const PracticeIdentity = ({ registerNameCheck }) => {
  const { setFieldValue, values, errors, initialValues } = useFormikContext();
  const { copyToClipboard } = useCopy();

  const imageHandlers = useMemo(() => {
    if (!values?.headerPhoto) return [];
    try {
      const parsed = JSON.parse(values.headerPhoto);
      const firstKey = Object.keys(parsed)[0];
      return firstKey ? [parsed[firstKey]] : [];
    } catch {
      return [];
    }
  }, [values?.headerPhoto]);

  const handleDBANameChange = (e) => {
    setFieldValue('dbaName', e?.target?.value);
  };

  return (
    <SectionList
      title="Practice identity"
      description="Set your logo, name, and contact details to personalize how your practice is displayed and reached.">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-500 text-neutral-900">Upload image</span>
            <span className="text-xs text-neutral-500">Min 400x400px PNG or JPEG</span>
          </div>
          <ImageUploader
            className="mt-1"
            handlers={imageHandlers}
            onDelete={() => {
              setFieldValue('headerPhoto', null);
            }}
            onUpload={(file) => {
              setFieldValue('headerPhoto', JSON.stringify({ jpg: file.handle }));
            }}
          />
        </div>

        {/* Practice Names */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Practice legal name"
            placeholder="Enter legal name"
            type="text"
            name="legal_name"
            value={values?.name}
            disabled
          />
          <PracticeNameInput
            field="dba_name"
            label={<span className="normal-case">DBA name</span>}
            type="text"
            placeholder="Enter DBA name"
            name="dba_name"
            value={values?.dbaName}
            savedValue={initialValues?.dbaName}
            onChange={handleDBANameChange}
            error={errors?.dbaName}
            keepAsTyped={!!values?.keepDbaNameAsTyped}
            onKeepAsTypedChange={(keep) => setFieldValue('keepDbaNameAsTyped', keep)}
            registerNameCheck={registerNameCheck}
            tooltip="The 'Doing Business As' (DBA) name entered here will be used in all patient communications, including appointment reminders and billing notices. If left blank, communications will default to the legal name of the practice. Use a DBA name that patients easily recognize to enhance clarity and branding in your interactions."
          />
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <Input
              label="Text-messaging number"
              type="text"
              disabled
              placeholder="Text-messaging number"
              name="twilio_phone"
              value={formatPhoneNumber(values?.twilio_phone) || 'No text-messaging number yet'}
            />
            {values?.twilio_phone && (
              <Icon
                icon="copy-mono"
                size={20}
                className="absolute right-3 top-[38px] cursor-pointer text-neutral-500 hover:text-primary-500"
                onClick={() => copyToClipboard(values?.twilio_phone)}
              />
            )}
          </div>
          <div className="relative">
            <Input
              label="E-fax number"
              type="text"
              disabled
              placeholder="E-fax number"
              name="fax_number"
              value={
                values?.eFax?.fax_number
                  ? formatPhoneNumber('+1' + values.eFax.fax_number)
                  : 'No fax number yet'
              }
            />
            {values?.eFax?.fax_number && (
              <Icon
                icon="copy-mono"
                size={20}
                className="absolute right-3 top-[38px] cursor-pointer text-neutral-500 hover:text-primary-500"
                onClick={() => copyToClipboard('+1' + values?.eFax?.fax_number)}
              />
            )}
          </div>
        </div>
      </div>
    </SectionList>
  );
};

export default withErrorBoundary(PracticeIdentity);
